import type { ColumnDef } from '@tanstack/react-table';
import React, { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import DataGridTable from '@components/DataGridTable/DataGridTable';
import Icon from '@components/common/Icon';
import {
    TableRowActionsTooltipProvider,
    TableRowIconActions,
} from '@components/common/TableRowIconActions';
import Button from '@components/Button/Button';
import Input from '@/components/Form/Input/Input';
import PageHeader from '@components/common/PageHeader';
import ConfirmModal from '@components/common/ConfirmModal/ConfirmModal';
import { useAuth } from '@/context/AuthContext';
import { getApiErrorMessage } from '@/utils/apiValidationErrors';
import moduleTemplatesService, {
    type ModuleTemplateDatum,
    type CreateModuleTemplatePayload,
    type UpdateModuleTemplatePayload,
} from '@/services/moduleTemplates/moduleTemplatesService';
import CreateModuleTemplateModal from './CreateModuleTemplateModal';
import EditModuleTemplateModal from './EditModuleTemplateModal';
import { getModuleIcon } from './iconHelper';

const ModuleTemplatesPage: React.FC = () => {
    const queryClient = useQueryClient();
    const { isSuperAdmin } = useAuth();

    // Permissions: Admins can manage templates
    const canManage = isSuperAdmin;

    // Filters and pagination state
    const [search, setSearch] = useState('');
    const [pageIndex, setPageIndex] = useState(0);
    const [debouncedSearch, setDebouncedSearch] = useState(search);
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [scopeFilter, setScopeFilter] = useState<'all' | 'global' | 'cohort_specific'>('all');

    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<ModuleTemplateDatum | null>(null);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const [pendingDeleteTitle, setPendingDeleteTitle] = useState<string>('');

    const PAGE_SIZE = 20;

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const isActiveQuery = statusFilter === 'all' ? undefined : statusFilter === 'active';
    const isCohortSpecificQuery =
        scopeFilter === 'all' ? undefined : scopeFilter === 'cohort_specific';

    const listQueryKey = [
        'module-templates',
        pageIndex,
        PAGE_SIZE,
        debouncedSearch,
        isActiveQuery,
        isCohortSpecificQuery,
    ];

    const {
        data: templatesResponse,
        isLoading,
        isError,
    } = useQuery({
        queryKey: listQueryKey,
        queryFn: () =>
            moduleTemplatesService.getModuleTemplates(
                pageIndex + 1,
                PAGE_SIZE,
                debouncedSearch.trim() || undefined,
                isActiveQuery,
                isCohortSpecificQuery,
            ),
        placeholderData: previousData => previousData,
        staleTime: 5 * 60 * 1000,
    });

    const templates = useMemo<ModuleTemplateDatum[]>(
        () => templatesResponse?.data?.data ?? [],
        [templatesResponse],
    );

    const totalCount = templatesResponse?.data?.total ?? 0;

    // Create mutation
    const createMutation = useMutation({
        mutationFn: (payload: CreateModuleTemplatePayload) =>
            moduleTemplatesService.createModuleTemplate(payload),
        onSuccess: res => {
            toast.success(res.message ?? 'Module template created successfully');
            setShowCreate(false);
            void queryClient.invalidateQueries({ queryKey: ['module-templates'] });
        },
        onError: (err: unknown) => {
            toast.error(getApiErrorMessage(err, 'Failed to create module template'));
        },
    });

    // Update mutation
    const updateMutation = useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: UpdateModuleTemplatePayload }) =>
            moduleTemplatesService.updateModuleTemplate(id, payload),
        onSuccess: res => {
            toast.success(res.message ?? 'Module template updated successfully');
            setShowEdit(false);
            setEditingTemplate(null);
            void queryClient.invalidateQueries({ queryKey: ['module-templates'] });
        },
        onError: (err: unknown) => {
            toast.error(getApiErrorMessage(err, 'Failed to update module template'));
        },
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: (id: string) => moduleTemplatesService.deleteModuleTemplate(id),
        onSuccess: res => {
            toast.success(res.message ?? 'Module template deleted successfully');
            setPendingDeleteId(null);
            void queryClient.invalidateQueries({ queryKey: ['module-templates'] });
        },
        onError: (err: unknown) => {
            toast.error(getApiErrorMessage(err, 'Failed to delete module template'));
        },
    });

    const columns = useMemo<ColumnDef<ModuleTemplateDatum, unknown>[]>(() => {
        const cols: ColumnDef<ModuleTemplateDatum, unknown>[] = [
            {
                accessorKey: 'order_no',
                header: 'Order',
                size: 80,
                cell: ({ row }) => (
                    <span className="font-bold text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        #{row.original.order_no}
                    </span>
                ),
            },
            {
                accessorKey: 'title',
                header: 'Module Title & Slug',
                size: 260,
                cell: ({ row }) => {
                    const template = row.original;
                    return (
                        <div className="flex flex-col gap-0.5">
                            <span className="font-bold text-[13px] text-[var(--secondary-color)]">
                                {template.title}
                            </span>
                            <span className="font-mono text-[11px] text-gray-500 bg-gray-50 border border-gray-200 px-1.5 py-0.2 rounded w-fit">
                                {template.slug}
                            </span>
                        </div>
                    );
                },
            },
            {
                accessorKey: 'route',
                header: 'Route',
                size: 200,
                cell: ({ row }) => (
                    <span className="font-mono text-[12px] text-primary">
                        {row.original.route}
                    </span>
                ),
            },
            {
                accessorKey: 'icon',
                header: 'Icon',
                size: 150,
                cell: ({ row }) => {
                    const IconComp = getModuleIcon(row.original.icon);
                    return (
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded bg-blue-50 text-primary flex items-center justify-center border border-blue-100">
                                <IconComp size={15} />
                            </div>
                            <span className="text-[12px] text-gray-700 font-medium">
                                {row.original.icon}
                            </span>
                        </div>
                    );
                },
            },
            {
                accessorKey: 'is_cohort_specific',
                header: 'Scope',
                size: 160,
                cell: ({ row }) => {
                    const isCohortSpecific = row.original.is_cohort_specific;
                    return (
                        <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                isCohortSpecific
                                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                    : 'bg-blue-50 text-blue-700 border border-blue-200'
                            }`}
                        >
                            {isCohortSpecific ? 'Cohort Specific' : 'Global Default'}
                        </span>
                    );
                },
            },
            {
                accessorKey: 'is_active',
                header: 'Status',
                size: 120,
                cell: ({ row }) => {
                    const isActive = row.original.is_active;
                    return (
                        <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                isActive
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                    : 'bg-gray-100 text-gray-600 border border-gray-200'
                            }`}
                        >
                            {isActive ? 'Active' : 'Inactive'}
                        </span>
                    );
                },
            },
        ];

        if (canManage) {
            cols.push({
                id: 'actions',
                header: 'Actions',
                size: 120,
                minSize: 120,
                enableSorting: false,
                cell: ({ row }) => {
                    const template = row.original;
                    const isCore = ['dashboard', 'recordings', 'coding-tasks', 'profile'].includes(
                        template.slug,
                    );
                    const actions = [
                        {
                            icon: 'edit' as const,
                            label: 'Edit Template',
                            onClick: () => {
                                setEditingTemplate(template);
                                setShowEdit(true);
                            },
                        },
                        ...(!isCore
                            ? [
                                  {
                                      icon: 'delete' as const,
                                      label: 'Delete Template',
                                      onClick: () => {
                                          setPendingDeleteId(template.id);
                                          setPendingDeleteTitle(template.title);
                                      },
                                  },
                              ]
                            : []),
                    ];

                    return (
                        <div className="flex items-center gap-1">
                            <TableRowIconActions actions={actions} />
                        </div>
                    );
                },
            });
        }

        return cols;
    }, [canManage]);

    return (
        <TableRowActionsTooltipProvider>
            <>
                <PageHeader
                    title="Module Templates"
                    breadcrumbItems={[
                        { label: 'Masters', to: '/admin/courses' },
                        { label: 'Module Templates' },
                    ]}
                    actions={
                        canManage ? (
                            <Button onClick={() => setShowCreate(true)}>
                                <Icon name="plus" className="mr-1 md:mr-2 mt-[-1px] w-3 2xl:w-4 h-auto" />
                                Create Template
                            </Button>
                        ) : undefined
                    }
                />

                <div className="flex flex-col gap-4 2xl:gap-5 !pt-0 p-3 md:p-4 2xl:p-5">
                    {/* Filter and Action Bar */}
                    <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 bg-white p-3.5 rounded-2xl border border-[var(--neutral-color)]">
                        <div className="flex flex-wrap items-center gap-2.5">
                            <div className="w-64">
                                <Input
                                    type="text"
                                    placeholder="Search by title, slug, or route..."
                                    value={search}
                                    onChange={e => {
                                        setSearch(e.target.value);
                                        setPageIndex(0);
                                    }}
                                    leftElement={<Icon name="search" className="w-3 2xl:w-[14px] h-auto" />}
                                />
                            </div>

                            {/* Status Filter */}
                            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg text-xs font-semibold">
                                {(['all', 'active', 'inactive'] as const).map(st => (
                                    <button
                                        key={st}
                                        type="button"
                                        onClick={() => {
                                            setStatusFilter(st);
                                            setPageIndex(0);
                                        }}
                                        className={`px-3 py-1.5 rounded-md capitalize transition-colors cursor-pointer ${
                                            statusFilter === st
                                                ? 'bg-white text-gray-900 shadow-xs'
                                                : 'text-gray-500 hover:text-gray-900'
                                        }`}
                                    >
                                        {st}
                                    </button>
                                ))}
                            </div>

                            {/* Scope Filter */}
                            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg text-xs font-semibold">
                                {(
                                    [
                                        { key: 'all', label: 'All Scopes' },
                                        { key: 'global', label: 'Global' },
                                        { key: 'cohort_specific', label: 'Cohort Specific' },
                                    ] as const
                                ).map(sc => (
                                    <button
                                        key={sc.key}
                                        type="button"
                                        onClick={() => {
                                            setScopeFilter(sc.key);
                                            setPageIndex(0);
                                        }}
                                        className={`px-3 py-1.5 rounded-md transition-colors cursor-pointer ${
                                            scopeFilter === sc.key
                                                ? 'bg-white text-gray-900 shadow-xs'
                                                : 'text-gray-500 hover:text-gray-900'
                                        }`}
                                    >
                                        {sc.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    {isError ? (
                        <div className="bg-white rounded-2xl border border-[var(--neutral-color)] p-4 text-sm text-[var(--semantic-error-color)]">
                            Failed to load module templates. Please refresh the page.
                        </div>
                    ) : (
                        <DataGridTable
                            columns={columns}
                            data={templates}
                            loading={isLoading}
                            totalCount={totalCount}
                            pageIndex={pageIndex}
                            pageSize={PAGE_SIZE}
                            onPageChange={setPageIndex}
                        />
                    )}

                    {/* Modals */}
                    <CreateModuleTemplateModal
                        isOpen={showCreate}
                        onClose={() => setShowCreate(false)}
                        onSubmit={async data => {
                            await createMutation.mutateAsync(data);
                        }}
                        isSubmitting={createMutation.isPending}
                    />

                    <EditModuleTemplateModal
                        isOpen={showEdit}
                        onClose={() => {
                            setShowEdit(false);
                            setEditingTemplate(null);
                        }}
                        initialData={editingTemplate}
                        onSubmit={async (id, data) => {
                            await updateMutation.mutateAsync({ id, payload: data });
                        }}
                        isSubmitting={updateMutation.isPending}
                    />

                    <ConfirmModal
                        isOpen={!!pendingDeleteId}
                        onClose={() => setPendingDeleteId(null)}
                        onConfirm={() => {
                            if (pendingDeleteId) {
                                deleteMutation.mutate(pendingDeleteId);
                            }
                        }}
                        title="Delete Module Template"
                        message={`Are you sure you want to delete "${pendingDeleteTitle}"? This will also remove it from any cohorts where it is assigned.`}
                        confirmLabel="Delete"
                        cancelLabel="Cancel"
                        loading={deleteMutation.isPending}
                    />
                </div>
            </>
        </TableRowActionsTooltipProvider>
    );
};

export default ModuleTemplatesPage;
